import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { MusicVideoEffectFamily } from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../../common/types';
import { storyMessages as messages } from '../../messages/storyMessages';
import { EFFECT_FAMILY_HINTS, EFFECT_FAMILY_NAMES, EFFECT_FAMILY_ORDER, toggleEffectFamily } from './familyMessages';
/* eslint-disable css-modules/no-unused-class -- the family colour classes are picked by name */
import styles from './EffectFamilyChips.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The colour of a family: the colours of the script legend (titles and shot effects share one). */
const TONE_CLASS: Readonly<Record<MusicVideoEffectFamily, string | undefined>> = {
  lyric: styles.lyric,
  title: undefined,
  transition: styles.transition,
  shot: undefined,
};

interface EffectFamilyChipsProps {
  families: readonly MusicVideoEffectFamily[];
  /** Switch the families on and off. Without it the chips are read only. */
  onChange?: (families: MusicVideoEffectFamily[]) => void;
  disabled?: boolean;
  /** Show the one-line hint under each chip. */
  withHints?: boolean;
  /** The group name for screen readers. */
  label: string;
}

/**
 * The four effect families of a style as chips: on = the director may use it in the script, off =
 * the script gets none of it (`alignEffects` of the server drops it). All four always show, so the
 * artist sees what is off.
 */
const EffectFamilyChipsComponent: React.FC<EffectFamilyChipsProps> = ({
  families,
  onChange,
  disabled = false,
  withHints = false,
  label,
}) => {
  const intl = useIntl();
  const baseId = useId();
  const isReadOnly = onChange === undefined;
  return (
    <div>
      <ul className={clsx(styles.list, !withHints && styles.compact)} aria-label={label}>
        {EFFECT_FAMILY_ORDER.map((family) => {
          const isOn = families.includes(family);
          const name = intl.formatMessage(EFFECT_FAMILY_NAMES[family]);
          const hintId = `${baseId}-${family}`;
          const inner = (
            <>
              <i className={styles.dot} aria-hidden />
              <span className={styles.name}>{name}</span>
              <span className={styles.state} aria-hidden={!isReadOnly}>
                {intl.formatMessage(isOn ? messages.familyOn : messages.familyOff)}
              </span>
            </>
          );
          return (
            <li key={family} className={styles.item}>
              {isReadOnly ? (
                <span className={clsx(styles.chip, TONE_CLASS[family], isOn && styles.on)}>{inner}</span>
              ) : (
                <button
                  type="button"
                  aria-pressed={isOn}
                  aria-label={name}
                  aria-describedby={withHints ? hintId : undefined}
                  className={clsx(styles.chip, styles.button, TONE_CLASS[family], isOn && styles.on)}
                  disabled={disabled}
                  onClick={() => onChange(toggleEffectFamily(families, family))}
                >
                  {inner}
                </button>
              )}
              {withHints && (
                <span id={hintId} className={styles.hint}>
                  {intl.formatMessage(EFFECT_FAMILY_HINTS[family])}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {families.length === 0 && <p className={styles.note}>{intl.formatMessage(messages.familiesNone)}</p>}
    </div>
  );
};

export const EffectFamilyChips = TypedMemo(EffectFamilyChipsComponent);
